import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Cell,
  CellType,
  createDemoVault,
  createEmptyVault,
  FletchyVault,
  Note,
  normalizeVault,
  searchVault,
} from "@fletchy/core";
import { EditorPane } from "./components/EditorPane";
import { NoteList } from "./components/NoteList";
import { Sidebar } from "./components/Sidebar";
import { createVaultExportFile, importVaultFile } from "./io/vaultFiles";
import { DexieStorageDriver } from "./storage/dexie";
import {
  addCellToNote,
  createNotebookWithInitialNote,
  createNoteInNotebook,
  deleteCellFromNote,
  firstActiveNoteId,
  moveCellInNote,
  renameNotebook,
  setVaultTheme,
  trashNote,
  updateCellInNote,
  updateNote,
  viewModeFor,
  ViewMode,
  ThemeMode,
} from "./state/vaultActions";
import { formatDate } from "./utils/format";

const params = new URLSearchParams(window.location.search);
const isDemoMode = params.has("demo");
const storage = new DexieStorageDriver(isDemoMode ? "fletchy-demo-v1" : "fletchy-local-v1", () =>
  isDemoMode ? createDemoVault() : createEmptyVault(),
);

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export default function App() {
  const [vault, setVault] = useState<FletchyVault | null>(null);
  const [selectedNotebookId, setSelectedNotebookId] = useState<string | "all">("all");
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("split");
  const [isSaving, setIsSaving] = useState(false);
  const [status, setStatus] = useState("Loading local vault...");
  const [importSummary, setImportSummary] = useState<string | null>(null);
  const [renamingNotebookId, setRenamingNotebookId] = useState<string | null>(null);
  const [notebookDraftTitle, setNotebookDraftTitle] = useState("");
  const hasLoadedRef = useRef(false);
  const skipNotebookRenameCommitRef = useRef(false);

  useEffect(() => {
    storage
      .loadVault()
      .then((loadedVault) => {
        setVault(loadedVault);
        setSelectedNotebookId(loadedVault.notebooks[0]?.id ?? "all");
        setSelectedNoteId(firstActiveNoteId(loadedVault));
        setViewMode(viewModeFor(loadedVault));
        setStatus(isDemoMode ? "Demo vault ready" : "Local vault ready");
        hasLoadedRef.current = true;
      })
      .catch((error: unknown) => {
        setStatus(error instanceof Error ? error.message : "Unable to load vault");
      });
  }, []);

  useEffect(() => {
    if (!vault || !hasLoadedRef.current) {
      return;
    }

    setIsSaving(true);
    const handle = window.setTimeout(() => {
      storage
        .saveVault(vault)
        .then(() => setStatus(`Saved ${formatDate(vault.updatedAt)}`))
        .catch((error: unknown) => setStatus(error instanceof Error ? error.message : "Unable to save vault"))
        .finally(() => setIsSaving(false));
    }, 350);

    return () => window.clearTimeout(handle);
  }, [vault]);

  const updateVault = useCallback((updater: (current: FletchyVault) => FletchyVault) => {
    setVault((current) => {
      if (!current) {
        return current;
      }

      return normalizeVault(updater(current));
    });
  }, []);

  const clearTransientNavigation = useCallback((nextVault: FletchyVault) => {
    setQuery("");
    setSelectedTag(null);
    setImportSummary(null);
    setRenamingNotebookId(null);
    setNotebookDraftTitle("");
    setViewMode(viewModeFor(nextVault));
  }, []);

  const notebooks = vault?.notebooks ?? [];
  const notes = vault?.notes.filter((note) => !note.trashedAt) ?? [];
  const cells = vault?.cells ?? [];
  const tags = useMemo(
    () => Array.from(new Set(notes.flatMap((note) => note.tags))).sort((a, b) => a.localeCompare(b)),
    [notes],
  );

  const searchHits = useMemo(() => (vault ? searchVault(vault, query) : []), [query, vault]);
  const searchOrder = useMemo(() => new Map(searchHits.map((hit, index) => [hit.noteId, index])), [searchHits]);

  const visibleNotes = useMemo(() => {
    let filtered = [...notes];

    if (selectedNotebookId !== "all") {
      filtered = filtered.filter((note) => note.notebookId === selectedNotebookId);
    }

    if (selectedTag) {
      filtered = filtered.filter((note) => note.tags.includes(selectedTag));
    }

    if (query.trim()) {
      filtered = filtered.filter((note) => searchOrder.has(note.id));
      filtered.sort((a, b) => (searchOrder.get(a.id) ?? 0) - (searchOrder.get(b.id) ?? 0));
      return filtered;
    }

    return filtered.sort((a, b) => {
      if (a.favorite !== b.favorite) {
        return a.favorite ? -1 : 1;
      }
      return b.updatedAt.localeCompare(a.updatedAt);
    });
  }, [notes, query, searchOrder, selectedNotebookId, selectedTag]);

  useEffect(() => {
    if (!vault) {
      return;
    }

    if (selectedNoteId && visibleNotes.some((note) => note.id === selectedNoteId)) {
      return;
    }

    setSelectedNoteId(visibleNotes[0]?.id ?? null);
  }, [selectedNoteId, vault, visibleNotes]);

  const selectedNote = notes.find((note) => note.id === selectedNoteId) ?? null;
  const selectedNotebook = notebooks.find((notebook) => notebook.id === selectedNotebookId);
  const selectedCells = selectedNote
    ? selectedNote.cellIds
        .map((cellId) => cells.find((cell) => cell.id === cellId))
        .filter((cell): cell is Cell => Boolean(cell))
        .sort((a, b) => a.sortOrder - b.sortOrder)
    : [];

  const createNotebookAction = () => {
    updateVault((current) => {
      const result = createNotebookWithInitialNote(current);
      setSelectedNotebookId(result.notebookId ?? "all");
      setSelectedNoteId(result.noteId);
      setSelectedTag(null);
      setQuery("");
      setImportSummary(null);
      setRenamingNotebookId(result.notebookId);
      setNotebookDraftTitle(result.notebookTitle);
      return result.vault;
    });
  };

  const createNoteAction = () => {
    if (!vault) {
      return;
    }

    const notebookId = selectedNotebookId === "all" ? notebooks[0]?.id : selectedNotebookId;
    if (!notebookId) {
      createNotebookAction();
      return;
    }

    updateVault((current) => {
      const result = createNoteInNotebook(current, notebookId);
      setSelectedNoteId(result.noteId);
      setRenamingNotebookId(null);
      setNotebookDraftTitle("");
      return result.vault;
    });
  };

  const updateSelectedNote = (patch: Partial<Note>) => {
    if (!selectedNote) {
      return;
    }

    updateVault((current) => updateNote(current, selectedNote.id, patch));
  };

  const addCell = (type: CellType) => {
    if (!selectedNote) {
      return;
    }

    updateVault((current) => addCellToNote(current, selectedNote.id, type).vault);
  };

  const updateCell = (updatedCell: Cell) => {
    if (!selectedNote) {
      return;
    }

    updateVault((current) => updateCellInNote(current, selectedNote.id, updatedCell));
  };

  const deleteCell = (cellId: string) => {
    if (!selectedNote || selectedNote.cellIds.length <= 1) {
      return;
    }

    updateVault((current) => deleteCellFromNote(current, selectedNote.id, cellId));
  };

  const moveCell = (cellId: string, direction: "up" | "down") => {
    if (!selectedNote) {
      return;
    }

    updateVault((current) => moveCellInNote(current, selectedNote.id, cellId, direction));
  };

  const deleteNote = () => {
    if (!selectedNote) {
      return;
    }

    updateVault((current) => trashNote(current, selectedNote.id));
  };

  const exportVault = () => {
    if (!vault) {
      return;
    }

    const exported = createVaultExportFile(vault);
    downloadBlob(exported.blob, exported.filename);
    setStatus(exported.status);
  };

  const importFile = async (file: File) => {
    if (!vault) {
      return;
    }

    try {
      const imported = await importVaultFile(vault, file);
      setVault(imported.vault);
      setSelectedNotebookId(imported.selectedNotebookId);
      setSelectedNoteId(imported.selectedNoteId);
      clearTransientNavigation(imported.vault);
      setImportSummary(imported.importSummary);
      setStatus(imported.status);
    } catch (error: unknown) {
      setImportSummary(error instanceof Error ? error.message : "Import failed");
      setStatus("Import failed");
    }
  };

  const resetDemo = async () => {
    const seed = isDemoMode ? createDemoVault() : createEmptyVault();
    const nextVault = await storage.resetVault(seed);
    setVault(nextVault);
    setSelectedNotebookId(nextVault.notebooks[0]?.id ?? "all");
    setSelectedNoteId(firstActiveNoteId(nextVault));
    clearTransientNavigation(nextVault);
    setStatus(isDemoMode ? "Demo vault reset" : "Local vault reset");
  };

  const updateTheme = (theme: ThemeMode) => {
    updateVault((current) => setVaultTheme(current, theme));
  };

  const commitNotebookRename = () => {
    if (skipNotebookRenameCommitRef.current) {
      skipNotebookRenameCommitRef.current = false;
      return;
    }

    if (!renamingNotebookId) {
      return;
    }

    const title = notebookDraftTitle.trim() || "New Notebook";
    updateVault((current) => renameNotebook(current, renamingNotebookId, title));
    setRenamingNotebookId(null);
    setNotebookDraftTitle("");
  };

  const cancelNotebookRename = () => {
    skipNotebookRenameCommitRef.current = true;
    setRenamingNotebookId(null);
    setNotebookDraftTitle("");
  };

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) {
        return;
      }

      if (event.key.toLowerCase() === "n" && event.shiftKey) {
        event.preventDefault();
        createNotebookAction();
      } else if (event.key.toLowerCase() === "n") {
        event.preventDefault();
        createNoteAction();
      } else if (event.key.toLowerCase() === "s") {
        event.preventDefault();
        exportVault();
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  });

  if (!vault) {
    return (
      <main className="loadingScreen">
        <div className="appMark">F</div>
        <p>{status}</p>
      </main>
    );
  }

  return (
    <main className="appShell" data-theme={vault.settings.theme}>
      <Sidebar
        isDemoMode={isDemoMode}
        notes={notes}
        notebooks={notebooks}
        tags={tags}
        selectedNotebookId={selectedNotebookId}
        selectedTag={selectedTag}
        renamingNotebookId={renamingNotebookId}
        notebookDraftTitle={notebookDraftTitle}
        onCreateNotebook={createNotebookAction}
        onExportVault={exportVault}
        onResetVault={resetDemo}
        onImportFile={(file) => void importFile(file)}
        onSelectAllNotes={() => {
          setSelectedNotebookId("all");
          setSelectedTag(null);
        }}
        onSelectNotebook={(notebookId) => {
          setSelectedNotebookId(notebookId);
          setSelectedTag(null);
        }}
        onToggleTag={(tag) => setSelectedTag(selectedTag === tag ? null : tag)}
        onNotebookDraftTitleChange={setNotebookDraftTitle}
        onCommitNotebookRename={commitNotebookRename}
        onCancelNotebookRename={cancelNotebookRename}
      />

      <NoteList
        cells={cells}
        notes={visibleNotes}
        query={query}
        selectedNoteId={selectedNoteId}
        title={selectedTag ? `#${selectedTag}` : selectedNotebook?.title ?? "All Notes"}
        onQueryChange={setQuery}
        onCreateNote={createNoteAction}
        onSelectNote={setSelectedNoteId}
      />

      <EditorPane
        selectedNote={selectedNote}
        selectedCells={selectedCells}
        viewMode={viewMode}
        theme={vault.settings.theme}
        isSaving={isSaving}
        status={status}
        importSummary={importSummary}
        onUpdateNote={updateSelectedNote}
        onAddCell={addCell}
        onUpdateCell={updateCell}
        onDeleteCell={deleteCell}
        onMoveCell={moveCell}
        onDeleteNote={deleteNote}
        onViewModeChange={setViewMode}
        onThemeChange={updateTheme}
        onCreateNote={createNoteAction}
      />
    </main>
  );
}
